import * as React from "react";
import isEqual from "lodash.isequal";
import debounce from "lodash.debounce";
import {
    EventBusConstructor,
    FiasComboBoxProps,
    FiasSchema,
    FiasValue,
    getAddressByString,
    getAddressText,
    ImportParams,
    importWidgetModule,
    WidgetApi,
} from "@skbkontur/fias-widget-npm-loader";
import { usePersistentCallback, WidgetRenderer } from "@skbkontur/widget-consumer-react-utils";
import { ValidationContainer, ValidationInfo, ValidationWrapper } from "@skbkontur/react-ui-validations";
import { Toast } from "react-ui";

import { useAsyncEffect } from "../../../../../../../Common/UseAsyncEffect";
import { resolveWidgetApiKey } from "../../../../../Helpers/WidgetUtils";
import { divisionType, ParsedExtendedFiasField } from "../../FiasInput/Types/FiasTypes";
import { AbsoluteResolvedModelPath } from "../../../../../../../Common/ModelPath/ModelPath";
import { FiasAddress, fiasWarningMessage } from "../../Utils/FiasAddressUtils";
import { FiasApi, RegionsType } from "../../FiasInput/FiasApi";

import { FiasDivision, FiasFullValue } from "./FiasWidgetTypes";
import { fiasFullValueToFiasAddress, resolveWidgetUrl } from "./Utils/FiasAddressBuilder";
import { FiasValueBuilder } from "./Utils/FiasValueBuilder";
import { WithValidationTransport } from "./WithValidationTransport";
import { convertDivisionType } from "./Utils/DivisionTypeConverter";
import { DEFAULT_FIAS_COUNTRY } from "./Utils/Constants";

export interface FiasWidgetProps {
    bindingPath: AbsoluteResolvedModelPath;
    apiUrl: string;
    portalUserId: string;
    division: divisionType;
    value: FiasAddress;
    stringAddress: string;
    isFormInitialized: boolean;
    fiasSchema: FiasSchema;
    divisionSelector: boolean;
    viewMode: Required<FiasComboBoxProps["viewMode"]>;
    countrySelector: boolean;
    countryCode: string;
    menuAlign?: "left" | "right";
    validationInfo: ValidationInfo | null;
    extendedFields: ParsedExtendedFiasField[];
    onValueChange: (nextValue: FiasAddress) => void;
    onRootValueChange: (value: string) => void;
    apiClient: FiasApi;
}

const WidgetContainerWithValidation = WithValidationTransport(WidgetContainer);

export const FiasWidget = (props: FiasWidgetProps) => {
    const apiUrlObj = new URL(props.apiUrl);

    const validationRef = React.useRef<ValidationContainer>(null);
    const [widgetApi, setWidgetApi] = React.useState<WidgetApi | null>(null);
    const regions = React.useRef<RegionsType>([]);
    const [shouldRerender, setShouldRerender] = React.useState(false);
    const [internalFiasAddress, setInternalFiasAddress] = React.useState(props.value);
    const [fiasInitialValue, setFiasInitialValue] = React.useState<FiasValue | null>(null);
    const [hasUnexpectedInputWarning, setHasUnexpectedInputWarning] = React.useState(false);

    const setFiasInitialValueWrapper = (nextValue: FiasValue | null) => {
        setFiasInitialValue(nextValue);
        callRerender();
    };

    const getRegions = async () => {
        if (regions.current.length === 0) {
            const regionsList = await props.apiClient.fetchRegions();
            regions.current = regionsList;
        }

        return regions.current;
    };

    const setInitialValueFromFiasAddress = async () => {
        const regionsList = await getRegions();
        const fiasQueryOptions = { division: convertDivisionType(props.division), schema: FiasSchema.Gar };

        let fiasAddressResult: Awaited<ReturnType<typeof getAddressByString>> | null = null;

        const fiasValueByAddress = FiasValueBuilder.buildValue(props.value, props.extendedFields, regionsList);

        if (Object.keys(fiasValueByAddress.address ?? {}).length > 0) {
            const addressText = getAddressText({ ...fiasQueryOptions, value: fiasValueByAddress });
            fiasAddressResult = await getAddressByString({
                addressString: addressText.value,
                baseUrl: apiUrlObj.href,
                query: fiasQueryOptions,
            });
        }

        if (!!fiasValueByAddress.foreignAddress && fiasValueByAddress.country?.code === DEFAULT_FIAS_COUNTRY.code) {
            fiasAddressResult = await getAddressByString({
                addressString: fiasValueByAddress.foreignAddress,
                baseUrl: apiUrlObj.href,
                query: fiasQueryOptions,
            });

            if (!fiasAddressResult?.success) {
                Toast.push("Не удалось заполнить адрес");
            }
        }

        if (fiasAddressResult?.success) {
            setFiasInitialValueWrapper({
                address: fiasAddressResult?.value.data ?? {},
            });
        } else {
            setFiasInitialValueWrapper(fiasValueByAddress);
        }
    };

    // Это, конечно, дичь, но это надо, чтобы виджет подхватывал изменения адреса снаружи
    // Ребята разрабы виджета ФИАС обещали завезти более адекватную поддержку через шину или еще как-то
    // Если ты тут что-то делаешь, уточни у них этот момент и поправь или заведи задачу, плиз
    const callRerender = debounce(() => {
        setShouldRerender(true);
        setTimeout(() => {
            setShouldRerender(false);
        }, 0);
    }, 200);

    useAsyncEffect(async () => {
        await setInitialValueFromFiasAddress();
    }, []);

    useAsyncEffect(async () => {
        if (!isEqual(internalFiasAddress, props.value)) {
            setInternalFiasAddress(props.value);
            await setInitialValueFromFiasAddress();
        }
    }, [props.value]);

    useAsyncEffect(async () => {
        if (!props.isFormInitialized) {
            return;
        }

        const importParams: ImportParams = {
            widgetUrl: new URL(resolveWidgetUrl()),
            apiUrl: apiUrlObj,
            apiKey: resolveWidgetApiKey(),
            userId: props.portalUserId,
        };
        const importResult = await importWidgetModule(importParams);
        if (importResult.success) {
            setWidgetApi(importResult.value);
        }
    }, [props.isFormInitialized]);

    if (widgetApi) {
        if (shouldRerender) {
            return null;
        }

        const handleValueChange = async (value: FiasFullValue) => {
            const regionsList = await getRegions();
            const fiasAddress = fiasFullValueToFiasAddress(value, props.extendedFields, regionsList);

            setInternalFiasAddress(fiasAddress);
            setHasUnexpectedInputWarning(false);
            props.onValueChange(fiasAddress);
        };

        const handleUnexpectedInput = (_: string) => {
            setHasUnexpectedInputWarning(true);
        };

        const validationInfo: ValidationInfo | null =
            props.validationInfo ??
            (hasUnexpectedInputWarning ? { level: "warning", type: "immediate", message: fiasWarningMessage } : null);

        return (
            <ValidationContainer ref={validationRef}>
                <ValidationWrapper validationInfo={validationInfo}>
                    <WidgetContainerWithValidation
                        widgetApi={widgetApi}
                        onValueChange={handleValueChange}
                        onUnexpectedInput={handleUnexpectedInput}
                        scope={props.bindingPath.toLegacyPath()}
                        initialValue={fiasInitialValue}
                        division={props.division}
                        fiasSchema={props.fiasSchema}
                        divisionSelector={props.divisionSelector}
                        viewMode={props.viewMode}
                        countrySelector={props.countrySelector}
                        countryCode={props.countryCode}
                        menuAlign={props.menuAlign}
                    />
                </ValidationWrapper>
            </ValidationContainer>
        );
    }

    return null;
};

interface WidgetContainerProps {
    widgetApi: WidgetApi;
    scope: string;
    eventBus: EventBusConstructor;
    initialValue: FiasValue | null;
    division: divisionType;
    fiasSchema: FiasSchema;
    divisionSelector: boolean;
    viewMode: Required<FiasComboBoxProps["viewMode"]>;
    countrySelector: boolean;
    countryCode: string;
    menuAlign?: "left" | "right";
}
function WidgetContainer(props: WidgetContainerProps) {
    const renderCallback = usePersistentCallback(
        async (container: HTMLElement) =>
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            props.widgetApi.renderFiasCombobox({
                container: container,
                eventBus: props.eventBus,
                scope: props.scope,
                props: {
                    initialValue: props.initialValue,
                    schema: props.fiasSchema,
                    division: props.division,
                    divisionSelector: props.divisionSelector,
                    viewMode: props.viewMode,
                    countrySelector: props.countrySelector,
                    initialFormValue: { countryCode: props.countryCode },
                    menuAlign: props.menuAlign,
                    fieldsSettings: {
                        intracityarea: ({ division }: FiasFullValue) =>
                            division === FiasDivision.Municipal ? { visible: false } : {},
                    },
                },
            }),
        [props.widgetApi, props.eventBus, props.scope]
    );

    return <WidgetRenderer renderWidget={renderCallback} loader={null} renderFault={() => null} />;
}
